class BeforeAfterComparison extends HTMLElement {
  #abortController = null;
  #frame = null;
  #range = null;
  #activePointerId = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#frame = this.querySelector('[data-before-after-frame]');
    this.#range = this.querySelector('[data-before-after-range]');

    if (!this.#frame || !this.#range) return;

    const { signal } = this.#abortController;

    this.#range.addEventListener('input', this.#handleRangeInput, { signal });
    this.#frame.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.#frame.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.#frame.addEventListener('pointerup', this.#handlePointerEnd, { signal });
    this.#frame.addEventListener('pointercancel', this.#handlePointerEnd, { signal });
    this.#frame.addEventListener('lostpointercapture', this.#handlePointerEnd, { signal });

    this.#setPosition(this.#range.value);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#activePointerId = null;
    this.#frame = null;
    this.#range = null;
  }

  #handleRangeInput = () => {
    this.#setPosition(this.#range?.value);
  };

  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    event.preventDefault();
    this.#activePointerId = event.pointerId;
    this.classList.add('is-dragging');
    this.#range.focus({ preventScroll: true });
    this.#frame.setPointerCapture(event.pointerId);
    this.#setPosition(this.#valueFromPointer(event));
  };

  #handlePointerMove = (event) => {
    if (event.pointerId !== this.#activePointerId) return;
    this.#setPosition(this.#valueFromPointer(event));
  };

  #handlePointerEnd = (event) => {
    if (this.#activePointerId !== null && event.pointerId !== this.#activePointerId) return;

    if (this.#activePointerId !== null && this.#frame?.hasPointerCapture(this.#activePointerId)) {
      this.#frame.releasePointerCapture(this.#activePointerId);
    }

    this.#activePointerId = null;
    this.classList.remove('is-dragging');
  };

  #valueFromPointer(event) {
    const bounds = this.#frame.getBoundingClientRect();

    if (this.dataset.orientation === 'vertical') {
      const y = Math.min(Math.max(event.clientY, bounds.top), bounds.bottom);
      return ((y - bounds.top) / Math.max(1, bounds.height)) * 100;
    }

    const x = Math.min(Math.max(event.clientX, bounds.left), bounds.right);
    const value = ((x - bounds.left) / Math.max(1, bounds.width)) * 100;
    return getComputedStyle(this).direction === 'rtl' ? 100 - value : value;
  }

  #setPosition(value) {
    if (!this.#range) return;

    const position = Math.min(100, Math.max(0, Number(value) || 0));
    const roundedPosition = Math.round(position);
    const beforeValue = roundedPosition;
    const afterValue = 100 - roundedPosition;

    this.style.setProperty('--before-after-position', `${position}%`);
    this.#range.value = String(roundedPosition);
    this.classList.toggle('is-label-before-visible', roundedPosition >= 62);
    this.classList.toggle('is-label-after-visible', roundedPosition <= 38);

    const valueTemplate = this.dataset.valueTemplate || '';
    this.#range.setAttribute(
      'aria-valuetext',
      valueTemplate
        .replace('[before_label]', this.dataset.beforeLabel || '')
        .replace('[before_value]', String(beforeValue))
        .replace('[after_label]', this.dataset.afterLabel || '')
        .replace('[after_value]', String(afterValue))
    );
  }
}

if (!customElements.get('before-after-comparison')) {
  customElements.define('before-after-comparison', BeforeAfterComparison);
}
